import { useEffect, useRef, useState } from "react";
import ComplianceNotice from "@/components/ComplianceNotice";
import { useSearchParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { ThinkingIndicator } from "@/components/ThinkingIndicator";
import {
  Send,
  Plus,
  Sparkles,
  MessageSquare,
  Trash2,
  Bot,
  User as UserIcon,
  ChevronRight,
  Menu,
  X,
} from "lucide-react";

function formatTs(iso) {
  try {
    return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return iso;
  }
}

const STARTER_PROMPTS = [
  "What should I be doing right now, this week?",
  "Help me build a 30-day fundraising plan.",
  "I have a candidate forum next Tuesday. How do I prep?",
  "How do I recruit my first 10 volunteers?",
  "Walk me through call time discipline.",
  "How do I get local press to cover my announcement?",
];

const STAFFER_STARTER_PROMPTS = [
  "How do I structure a volunteer phone bank?",
  "Build me a GOTV plan for the final weekend.",
  "How many door-knockers do I need to hit 5,000 doors in three weeks?",
  "How should I organize our lawn sign distribution?",
  "Help me write a recruitment ask for new canvassers.",
  "What's a good agenda for a volunteer training night?",
];

export default function Advisor() {
  const [sessions, setSessions] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [active, setActive] = useState(null); // { id, title, messages: [] }
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingList, setLoadingList] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [accountType, setAccountType] = useState(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const bootstrappedRef = useRef(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const starterPrompts = accountType === "staffer" ? STAFFER_STARTER_PROMPTS : STARTER_PROMPTS;

  useEffect(() => {
    api.get("/campaign").then(({ data }) => setAccountType(data?.account_type || null)).catch(() => {});
  }, []);

  const loadList = async () => {
    if (bootstrappedRef.current) return;
    bootstrappedRef.current = true;
    setLoadingList(true);
    const prefillPrompt = (searchParams.get("prompt") || "").trim();
    const forceNew = searchParams.get("new") === "1";
    try {
      const { data } = await api.get("/advisor/sessions");
      setSessions(data || []);
      if (prefillPrompt && forceNew) {
        // Fresh session for the help-from-checklist flow so the question is the first message.
        await createSession();
        setDraft(prefillPrompt);
        // Clear the URL params so refreshes don't re-create another session.
        const next = new URLSearchParams(searchParams);
        next.delete("prompt");
        next.delete("new");
        next.delete("from");
        setSearchParams(next, { replace: true });
        // Focus composer so user can edit or send.
        setTimeout(() => inputRef.current?.focus(), 0);
      } else if ((data || []).length === 0) {
        await createSession();
      } else if (!activeId) {
        await openSession(data[0].id);
      }
    } catch (err) {
      bootstrappedRef.current = false; // allow retry on failure
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoadingList(false);
    }
  };

  const openSession = async (sid) => {
    setActiveId(sid);
    setSidebarOpen(false);
    try {
      const { data } = await api.get(`/advisor/sessions/${sid}`);
      setActive(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const createSession = async () => {
    try {
      const { data } = await api.post("/advisor/sessions");
      setActive(data);
      setActiveId(data.id);
      setSessions((prev) => {
        const filtered = prev.filter((s) => s.id !== data.id);
        return [
          { id: data.id, title: data.title, message_count: 0, updated_at: data.updated_at, created_at: data.created_at },
          ...filtered,
        ];
      });
      setSidebarOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const deleteSession = async (sid) => {
    try {
      await api.delete(`/advisor/sessions/${sid}`);
      const remaining = sessions.filter((s) => s.id !== sid);
      setSessions(remaining);
      if (activeId === sid) {
        if (remaining.length > 0) {
          await openSession(remaining[0].id);
        } else {
          await createSession();
        }
      }
      toast.success("Conversation deleted");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  useEffect(() => {
    loadList();
  }, []);

  // Auto-scroll on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [active?.messages?.length, sending]);

  const send = async (text) => {
    const content = (text ?? draft).trim();
    if (!content || !activeId || sending) return;
    setDraft("");
    // Optimistically append user message
    const optimisticUser = { role: "user", content, ts: new Date().toISOString() };
    setActive((prev) => prev ? { ...prev, messages: [...prev.messages, optimisticUser] } : prev);
    setSending(true);
    try {
      const { data } = await api.post(`/advisor/sessions/${activeId}/messages`, { message: content });
      setActive((prev) => prev ? {
        ...prev,
        title: data.title,
        messages: [...prev.messages, data.assistant_message],
      } : prev);
      // Update session list summary
      setSessions((prev) => prev.map((s) =>
        s.id === activeId
          ? { ...s, title: data.title, message_count: (s.message_count || 0) + 2, updated_at: data.assistant_message.ts }
          : s,
      ).sort((a, b) => (b.updated_at || "").localeCompare(a.updated_at || "")));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      // Roll back optimistic user message
      setActive((prev) => prev ? { ...prev, messages: prev.messages.slice(0, -1) } : prev);
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const isEmpty = !active || (active.messages?.length || 0) === 0;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <p className="text-sm font-semibold uppercase tracking-wider text-flag-red">Campaign Advisor</p>
          <h1 className="font-display text-3xl sm:text-4xl font-black text-flag-navy mt-1">Your strategist on call.</h1>
          <p className="text-slate-600 mt-2">Ask anything about running this race. Grounded in your profile.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setSidebarOpen((v) => !v)}
            data-testid="advisor-sidebar-toggle"
            className="md:hidden h-10 border-2 font-semibold"
          >
            {sidebarOpen ? <X className="h-4 w-4 mr-1.5" /> : <Menu className="h-4 w-4 mr-1.5" />}
            Chats
          </Button>
          <Button
            onClick={createSession}
            data-testid="advisor-new-chat-btn"
            className="h-10 px-4 bg-flag-red hover:bg-flag-red-hover text-white font-bold"
          >
            <Plus className="h-4 w-4 mr-1.5" /> New chat
          </Button>
        </div>
      </div>

      <ComplianceNotice kind="ai" className="mb-4" />

      <div className="grid md:grid-cols-[260px_1fr] gap-4 h-[70vh] min-h-[520px]">
        {/* Sidebar */}
        <aside
          data-testid="advisor-sidebar"
          className={`${sidebarOpen ? "block" : "hidden md:block"} bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden flex flex-col`}
        >
          <div className="px-4 py-3 border-b border-slate-100 text-xs font-bold uppercase tracking-wider text-slate-500">
            Conversations
          </div>
          <div className="flex-1 overflow-y-auto" data-testid="advisor-sessions-list">
            {loadingList ? (
              <div className="p-4 text-sm text-slate-500">Loading…</div>
            ) : sessions.length === 0 ? (
              <div className="p-4 text-sm text-slate-500">No conversations yet.</div>
            ) : (
              <ul className="py-2">
                {sessions.map((s) => (
                  <li key={s.id} className="px-2">
                    <div
                      className={`group flex items-center gap-2 rounded-md px-2 py-2 cursor-pointer ${
                        activeId === s.id ? "bg-flag-navy/5" : "hover:bg-slate-50"
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() => openSession(s.id)}
                        data-testid={`advisor-session-${s.id}`}
                        className="flex-1 text-left min-w-0"
                      >
                        <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-800 truncate">
                          <MessageSquare className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                          <span className="truncate">{s.title}</span>
                        </div>
                        <div className="text-[11px] text-slate-500 mt-0.5">{formatTs(s.updated_at)}</div>
                      </button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <button
                            type="button"
                            data-testid={`advisor-session-delete-${s.id}`}
                            className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded text-slate-400 hover:text-flag-red"
                            aria-label="Delete conversation"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete this conversation?</AlertDialogTitle>
                            <AlertDialogDescription>It will be permanently removed.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => deleteSession(s.id)}
                              className="bg-flag-red hover:bg-flag-red-hover"
                              data-testid={`advisor-session-delete-confirm-${s.id}`}
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </aside>

        {/* Chat panel */}
        <section className="bg-white border border-slate-200 rounded-xl shadow-sm flex flex-col overflow-hidden" data-testid="advisor-chat-panel">
          <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-5" ref={scrollRef} data-testid="advisor-messages-scroll">
            {isEmpty ? (
              <div className="h-full flex flex-col items-center justify-center text-center" data-testid="advisor-empty">
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-flag-red text-white">
                  <Bot className="h-6 w-6" />
                </span>
                <h2 className="mt-4 font-display text-2xl font-bold text-flag-navy">Ask me anything about your race.</h2>
                <p className="mt-2 text-slate-600 max-w-md">Strategy, fundraising, field, press, debate prep — I'm grounded in your profile.</p>
                <div className="mt-6 grid sm:grid-cols-2 gap-2 max-w-xl w-full">
                  {starterPrompts.map((p, i) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => send(p)}
                      data-testid={`advisor-starter-${i}`}
                      className="text-left bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-lg p-3 text-sm font-medium text-slate-800 flex items-center justify-between gap-2 transition-colors"
                    >
                      <span className="truncate">{p}</span>
                      <ChevronRight className="h-4 w-4 text-slate-400 flex-shrink-0" />
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <ul className="space-y-5" data-testid="advisor-messages-list">
                {active.messages.map((m, i) => (
                  <li key={`${m.ts}-${m.role}`} className="flex gap-3" data-testid={`advisor-msg-${m.role}-${i}`}>
                    <span
                      className={`flex-shrink-0 inline-flex h-8 w-8 items-center justify-center rounded-md ${
                        m.role === "assistant" ? "bg-flag-red text-white" : "bg-slate-200 text-slate-700"
                      }`}
                    >
                      {m.role === "assistant" ? <Bot className="h-4 w-4" /> : <UserIcon className="h-4 w-4" />}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                        {m.role === "assistant" ? "Campaign Advisor" : "You"}
                      </div>
                      {m.role === "assistant" ? (
                        <div className="advisor-prose mt-1 text-slate-900 leading-relaxed">
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown>
                        </div>
                      ) : (
                        <div className="mt-1 text-slate-900 leading-relaxed whitespace-pre-wrap">
                          {m.content}
                        </div>
                      )}
                    </div>
                  </li>
                ))}
                {sending && (
                  <li className="flex gap-3" data-testid="advisor-thinking">
                    <span className="flex-shrink-0 inline-flex h-8 w-8 items-center justify-center rounded-md bg-flag-red text-white">
                      <Bot className="h-4 w-4" />
                    </span>
                    <ThinkingIndicator variant="inline" label="Thinking" testid="advisor-thinking-indicator" />
                  </li>
                )}
              </ul>
            )}
          </div>

          {/* Composer */}
          <div className="border-t border-slate-100 bg-slate-50/40 p-3 sm:p-4">
            <div className="relative">
              <Textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={onKeyDown}
                rows={2}
                placeholder='Ask anything — e.g. "What should I be doing right now?"'
                className="resize-none bg-white pr-28 text-base"
                data-testid="advisor-input"
                disabled={!activeId || sending}
                ref={inputRef}
              />
              <Button
                onClick={() => send()}
                disabled={!activeId || sending || !draft.trim()}
                data-testid="advisor-send-btn"
                className="absolute bottom-2 right-2 h-10 px-3 bg-flag-red hover:bg-flag-red-hover text-white font-bold"
              >
                <Send className="h-4 w-4 mr-1.5" /> Send
              </Button>
            </div>
            <p className="mt-2 text-[11px] text-slate-500">Press Enter to send · Shift+Enter for a new line</p>
          </div>
        </section>
      </div>
    </div>
  );
}
